import * as THREE from 'three';

// Rare, "event" meteors — distinct from the quick line-streaks in
// shootingStars.js. Layered visuals:
//   • a small hot near-white core sphere,
//   • a larger warm glow halo with fresnel so the center reads bright and
//     the silhouette fades out — looks volumetric, not like a solid ball,
//   • a cone-shaped trail that's thickest at the head and tapers out into
//     nothing, with a soft side-glow from a second fresnel term.
// All additive, all pulsing gently so the fireball looks like it's burning.

const METEOR_POOL = 2;
const METEOR_MIN_DELAY = 25;
const METEOR_MAX_DELAY = 70;

const meteors = [];
let nextSpawn = 10;    // first one a few seconds after load, not on tick 0

// Shared geometries — scaled per meteor.
const CORE_GEO = new THREE.SphereGeometry(0.55, 20, 14);
const GLOW_GEO = new THREE.SphereGeometry(2.2,  24, 18);
// Cone: apex along +Y, base at y=0 so scaling Y stretches the tail backward.
const TRAIL_GEO = new THREE.ConeGeometry(1, 1, 18, 1, true);
TRAIL_GEO.translate(0, 0.5, 0);

const _up = new THREE.Vector3(0, 1, 0);
const _tailDir = new THREE.Vector3();

function makeGlowMaterial() {
  return new THREE.ShaderMaterial({
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    uniforms: {
      uColor: { value: new THREE.Color(0xffaa55) },
      uAlpha: { value: 0 },
    },
    vertexShader: /* glsl */`
      varying vec3 vN;
      varying vec3 vV;
      void main() {
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vN = normalize(normalMatrix * normal);
        vV = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform vec3 uColor;
      uniform float uAlpha;
      varying vec3 vN;
      varying vec3 vV;
      void main() {
        // Brightest where the sphere faces the camera (centre of the visible
        // disc), falling to zero at the silhouette — reads as a soft glow
        // orb with additive blending.
        float facing = abs(dot(vN, vV));
        gl_FragColor = vec4(uColor, pow(facing, 1.3) * uAlpha);
      }
    `,
  });
}

function makeTrailMaterial() {
  return new THREE.ShaderMaterial({
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: {
      uColor: { value: new THREE.Color(0xffaa55) },
      uAlpha: { value: 0 },
    },
    vertexShader: /* glsl */`
      varying float vT;         // 0 at base (head), 1 at apex (tail end)
      varying vec3 vN;
      varying vec3 vV;
      void main() {
        vT = position.y;        // geometry pre-built to span 0..1 along Y
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vN = normalize(normalMatrix * normal);
        vV = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform vec3 uColor;
      uniform float uAlpha;
      varying float vT;
      varying vec3 vN;
      varying vec3 vV;
      void main() {
        // Length fade: bright at head, gone at tail.
        float lengthFade = pow(1.0 - vT, 1.7);
        // Fresnel rim: softer at the cone's silhouette so the trail feels
        // like gas rather than a solid cone shell.
        float rim = 1.0 - abs(dot(vN, vV));
        float softShape = 0.2 + 0.55 * pow(rim, 1.3);
        gl_FragColor = vec4(uColor, lengthFade * softShape * uAlpha);
      }
    `,
  });
}

export function createMeteors(scene) {
  for (let i = 0; i < METEOR_POOL; i++) {
    // Head = hot core + warm glow, grouped so we can scale them together.
    const coreMat = new THREE.MeshBasicMaterial({
      color: 0xfff3d0,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
    });
    const core = new THREE.Mesh(CORE_GEO, coreMat);

    const glowMat = makeGlowMaterial();
    const glow = new THREE.Mesh(GLOW_GEO, glowMat);

    const head = new THREE.Group();
    head.add(core);
    head.add(glow);
    head.frustumCulled = false;
    head.visible = false;
    scene.add(head);

    // Trail cone, oriented per-frame along -velocity.
    const trailMat = makeTrailMaterial();
    const trail = new THREE.Mesh(TRAIL_GEO, trailMat);
    trail.frustumCulled = false;
    trail.visible = false;
    scene.add(trail);

    meteors.push({
      active: false,
      head, core, glow, trail,
      coreMat, glowMat, trailMat,
      pos: new THREE.Vector3(),
      vel: new THREE.Vector3(),
      life: 0,
      maxLife: 0,
      tailLen: 0,
      color: new THREE.Color(),
      flickerPhase: 0,
    });
  }
}

function spawn() {
  const m = meteors.find((x) => !x.active);
  if (!m) return;

  // Start on a far shell, moving roughly tangent to it so the meteor crosses
  // the visible sky rather than flying at the camera.
  const startDir = new THREE.Vector3().randomDirection();
  m.pos.copy(startDir).multiplyScalar(260);

  const velDir = new THREE.Vector3().randomDirection();
  velDir.addScaledVector(startDir, -velDir.dot(startDir)).normalize();
  m.vel.copy(velDir).multiplyScalar(55 + Math.random() * 45);   // slower than shooting stars

  m.maxLife = 2.6 + Math.random() * 1.6;                        // much longer visible life
  m.life    = m.maxLife;
  m.tailLen = 90 + Math.random() * 70;                          // longer trail for the cone

  // Warm fireball tint — amber through deep orange.
  const warmth = Math.random();
  m.color.setRGB(1.0, 0.55 + warmth * 0.3, 0.25 + warmth * 0.25);

  m.flickerPhase = Math.random() * Math.PI * 2;

  m.active = true;
  m.head.visible = true;
  m.trail.visible = true;
}

export function updateMeteors(dt) {
  nextSpawn -= dt;
  if (nextSpawn <= 0) {
    spawn();
    nextSpawn = METEOR_MIN_DELAY + Math.random() * (METEOR_MAX_DELAY - METEOR_MIN_DELAY);
  }

  const now = performance.now();

  for (const m of meteors) {
    if (!m.active) continue;

    m.pos.addScaledVector(m.vel, dt);
    m.life -= dt;
    if (m.life <= 0) {
      m.active = false;
      m.head.visible = false;
      m.trail.visible = false;
      continue;
    }

    // Longer ease-in than shooting stars so the meteor feels like it's
    // burning in rather than flickering on.
    const t = m.life / m.maxLife;
    const envelope = t < 0.22 ? (t / 0.22) : Math.min(1, t * 1.5);

    // Subtle fire flicker — a quick low-amplitude sine so the whole thing
    // breathes. Different phase per meteor.
    const flicker = 1.0 + 0.18 * Math.sin(now * 0.02 + m.flickerPhase);

    // ---- Head ----
    m.head.position.copy(m.pos);
    m.coreMat.opacity = envelope * 0.95 * flicker;
    m.glowMat.uniforms.uColor.value.copy(m.color);
    m.glowMat.uniforms.uAlpha.value = envelope * 0.7 * flicker;
    m.head.scale.setScalar((0.7 + envelope * 0.45) * flicker);

    // ---- Trail ----
    _tailDir.copy(m.vel).normalize().negate();
    m.trail.position.copy(m.pos);
    m.trail.quaternion.setFromUnitVectors(_up, _tailDir);
    // Scale: x/z set trail width, y sets trail length.
    m.trail.scale.set(1.1, m.tailLen, 1.1);
    m.trailMat.uniforms.uColor.value.copy(m.color);
    m.trailMat.uniforms.uAlpha.value = envelope * 0.95 * flicker;
  }
}
